import {
  HelpIcon,
  Icon,
  IconNames,
  Switch,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { useState } from 'react';

import styles from './presenceDropdownItem.module.scss';

type PresenceDropdownItemProps = {
  /** Icon to display in the item. */
  icon: IconNames | JSX.Element;
  /** Text to display in the item. */
  text: string;
  /** Text for the tooltip displayed when hovering the whole item. */
  tooltipText?: string;
  /** Body text for the popover displayed when hovering an info icon. */
  helpText?: JSX.Element | string;
  /** Title for the popover displayed when hovering an info icon. */
  helpTitle?: string;
  /** Elements to render in the action area to the right of the item. */
  children?: JSX.Element;
  /** Function to call when the item is clicked. */
  onClick?: () => void;
  /** Subitems to render below the item. */
  subitems?: JSX.Element[];
  /** Whether the item is a subitem. */
  isSubitem?: boolean;
  /** An ID for the description element, to use in tests. */
  descriptionTestId?: string;
  /** Indicates if the item is disabled. */
  disabled?: boolean;
  /** Indicates whether subchannel is open initially */
  isSubchannelOpen?: boolean;
  /** DOM element ID for ARIA accessibility (aria-activedescendant). */
  itemId?: string;
  /** The currently focused item's ID (for keyboard navigation visual indicator). */
  focusedItemId?: string;
} & PropsWithClassName;

export const PresenceDropdownItem = ({
  className,
  icon,
  text,
  tooltipText,
  helpTitle,
  helpText,
  children,
  onClick,
  subitems,
  isSubitem = false,
  isSubchannelOpen = false,
  descriptionTestId,
  disabled = false,
  itemId,
  focusedItemId,
}: PresenceDropdownItemProps): JSX.Element => {
  const [isSubchannelsOpen, setIsSubchannelsOpen] = useState(isSubchannelOpen);
  const isFocused = itemId != null && itemId === focusedItemId;

  const content = (
    <li
      id={itemId}
      role="menuitem"
      aria-disabled={disabled ? 'true' : undefined}
      className={classNames(styles.item, className, {
        [styles.itemSubItem]: isSubitem,
        [styles.itemDisabled]: disabled,
        [styles.itemFocused]: isFocused,
      })}
      onClick={onClick}
    >
      <span className={styles.title}>
        {typeof icon === 'string' ? (
          <Icon
            className={styles.icon}
            name={icon}
            size={20}
            color="secondary"
          />
        ) : (
          icon
        )}

        <Text.Paragraph
          as="span"
          variant="2"
          className={styles.text}
          data-testid={descriptionTestId}
        >
          {text}
        </Text.Paragraph>

        {helpText ? (
          <HelpIcon
            tooltipClassName={styles.helpPopover}
            title={helpTitle}
            text={helpText}
            side="right"
          />
        ) : null}
      </span>

      <span className={styles.actions}>
        {children}

        {subitems && subitems.length > 0 && (
          <Icon
            name="angle-down"
            className={classNames(styles.accordionIcon, {
              [styles.accordionIconOpen]: isSubchannelsOpen,
            })}
            onClick={() => setIsSubchannelsOpen(!isSubchannelsOpen)}
          />
        )}
      </span>
    </li>
  );

  return (
    <>
      <Tooltip disabled={!tooltipText} content={tooltipText}>
        {content}
      </Tooltip>
      {isSubchannelsOpen && subitems}
    </>
  );
};

type PresenceToggleProps = {
  /** Indicates if the toggle is active/checked or not. */
  active: boolean;
  /** Function to call when the toggle is clicked. */
  onToggle: () => void;
  /** Indicates if the agent is currently away, to style the color of the toggle. */
  isAway?: boolean;
  /** Text for the tooltip displayed when hovering the toggle button. */
  tooltipText?: string;
  /** An ID for the Switch element. Prefixed with `presence-toggle`. */
  id: string;
  /** Indicates if the toggle is disabled. */
  disabled?: boolean;
};

export const PresenceToggle = ({
  active,
  onToggle,
  isAway,
  tooltipText,
  id,
  disabled = false,
}: PresenceToggleProps): JSX.Element => {
  return (
    <Tooltip content={tooltipText}>
      <Switch
        id={`presence-toggle-${id.toLowerCase().replace(/\s/g, '-')}`}
        data-testid={`presence-toggle-${id.toLowerCase().replace(/\s/g, '-')}`}
        className={styles.presenceSwitch}
        variant={isAway ? 'pending' : 'active'}
        checked={active}
        onChange={onToggle}
        value=""
        name=""
        disabled={disabled}
      />
    </Tooltip>
  );
};
